import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  EVENT_LABELS,
  useCalendarActions,
  type CalendarEvent,
  type CalendarEventType,
} from '@/features/calendar/api';
import { useDoctors } from '@/features/queue/api';
import { isApiError } from '@/lib/api';
import { affectedText, parseShortHours, recreateInput } from './event-format';
import { todayKl } from './time';
import styles from './calendar.module.css';

const TYPES: CalendarEventType[] = [
  'doctor_leave',
  'clinic_closure',
  'short_hours',
  'public_holiday',
  'ramadan_hours',
];

/**
 * "Add to the calendar" (#18): a public holiday, doctor's leave, closure,
 * short hours or Ramadan hours, with dates, branch and doctor. "Save and tell
 * patients" saves it; patients booked in that time get a message to rebook.
 *
 * With `replaces` it changes a saved entry (RL-1047): the form starts from
 * that entry and saving removes it and adds the new one (there is no edit
 * API). If the new one is refused, the original is added back.
 */
export function AddEventDrawer({
  open,
  onClose,
  replaces = null,
}: {
  open: boolean;
  onClose: () => void;
  replaces?: CalendarEvent | null;
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const { selected } = useBranch();
  const branches = me?.branches ?? [];
  const { data: doctors } = useDoctors();
  const a = useCalendarActions();
  const toast = useToast();
  const hours = parseShortHours(replaces?.shortHours ?? null);
  const [type, setType] = useState<CalendarEventType>(
    replaces?.type ?? 'doctor_leave',
  );
  const [from, setFrom] = useState(replaces?.from ?? todayKl());
  const [to, setTo] = useState(replaces?.to ?? todayKl());
  const [branch, setBranch] = useState<string>(
    replaces
      ? (replaces.branch?.id ?? 'both')
      : (selected?.id ?? branches[0]?.id ?? ''),
  );
  const [doctor, setDoctor] = useState(replaces?.doctor?.id ?? '');
  const [description, setDescription] = useState(replaces?.description ?? '');
  const [opens, setOpens] = useState(hours?.opens ?? '08:00');
  const [closes, setCloses] = useState(hours?.closes ?? '13:00');

  const [failure, setFailure] = useState<{
    title: string;
    message: string;
    requestId?: string;
  } | null>(null);

  const failed = (err: unknown, title: string, after = '') =>
    setFailure(
      isApiError(err)
        ? {
            // e.g. 409 when the doctor already has leave in these dates (RL-1046).
            title: err.status === 409 ? (err.problem?.title ?? title) : title,
            message: `${err.message}${after}`,
            requestId: err.requestId,
          }
        : { title, message: `Check the entry and try again.${after}` },
    );

  async function save() {
    setFailure(null);
    const input = {
      type,
      from,
      to,
      branchId: branch === 'both' ? null : branch,
      doctorStaffId: type === 'doctor_leave' ? doctor : undefined,
      description: description.trim(),
      ...(type === 'short_hours' ? { opens, closes } : {}),
    };
    if (replaces) {
      try {
        await a.removeEvent.mutateAsync(replaces.id);
      } catch (err) {
        failed(err, 'Not changed');
        return;
      }
    }
    try {
      const e = await a.addEvent.mutateAsync(input);
      toast.show({
        tone: 'success',
        title: replaces
          ? `${EVENT_LABELS[e.type]} changed`
          : `${EVENT_LABELS[e.type]} saved`,
        body: affectedText(e.affectedCount, e.affected),
      });
      setDescription('');
      onClose();
    } catch (err) {
      // Shown in the drawer (not a passing toast) so the entry can be fixed.
      if (!replaces) return failed(err, 'Not saved');
      const restored = await a.addEvent
        .mutateAsync(recreateInput(replaces))
        .then(() => true)
        .catch(() => false);
      failed(
        err,
        'Not changed',
        restored
          ? ' The original entry was put back.'
          : ' The original entry was removed and could not be added back: add it again.',
      );
    }
  }

  const valid =
    from &&
    to &&
    to >= from &&
    description.trim().length >= 2 &&
    (type !== 'doctor_leave' || doctor);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={replaces ? 'Change calendar entry' : 'Add to the calendar'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={<Check size={16} />}
            disabled={!valid}
            loading={a.addEvent.isPending || a.removeEvent.isPending}
            onClick={() => void save()}
          >
            Save and tell patients
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {failure && (
          <div className={styles.note} role="alert">
            <strong>{failure.title}</strong>
            <br />
            {failure.message}
            {failure.requestId && (
              <span className={styles.requestId}>
                {t('common.requestId', { id: failure.requestId })}
              </span>
            )}
          </div>
        )}
        <div className={styles.section}>
          <p className="eyebrow">Type</p>
          <ChipToggle
            label="Type"
            single
            options={TYPES.map((t) => ({ value: t, label: EVENT_LABELS[t] }))}
            selected={[type]}
            onChange={([t]) => setType(t)}
          />
        </div>
        <Field label="From">
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label="To">
          <Input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
        {type === 'short_hours' && (
          <div className={styles.split}>
            <Field label="Opens">
              <Input
                type="time"
                value={opens}
                onChange={(e) => setOpens(e.target.value)}
              />
            </Field>
            <Field label="Closes">
              <Input
                type="time"
                value={closes}
                onChange={(e) => setCloses(e.target.value)}
              />
            </Field>
          </div>
        )}
        <div className={styles.section}>
          <p className="eyebrow">Branch</p>
          <ChipToggle
            label="Branch"
            single
            options={[
              ...branches.map((b) => ({ value: b.id, label: b.name })),
              ...(branches.length > 1
                ? [{ value: 'both', label: 'Both' }]
                : []),
            ]}
            selected={[branch]}
            onChange={([b]) => setBranch(b)}
          />
        </div>
        {type === 'doctor_leave' && (
          <Field label="Doctor (for leave)">
            <Select
              value={doctor}
              onChange={(e) => {
                setDoctor(e.target.value);
                const d = doctors?.find((x) => x.id === e.target.value);
                if (d && !description) setDescription(`${d.fullName} on leave`);
              }}
              placeholder="Choose the doctor"
              options={(doctors ?? []).map((d) => ({
                value: d.id,
                label: d.fullName,
              }))}
            />
          </Field>
        )}
        <Field label="Description">
          <Input
            value={description}
            placeholder={
              type === 'public_holiday'
                ? 'Malaysia Day'
                : 'Doctor on annual leave'
            }
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        {replaces && (
          <p className={styles.sub}>
            Saving removes the original entry and adds this one; patients are
            told about bookings the new dates affect.
          </p>
        )}
        <p className={styles.sub}>
          Bookings in this period that can no longer go ahead are marked to
          rebook and the patients are told straight away. For doctor leave,
          &ldquo;any doctor&rdquo; bookings on those days are counted too, for
          you to check. A doctor cannot have two leave entries for the same
          days. (Messages are recorded in the outbox; no SMS or WhatsApp
          provider is connected yet.)
        </p>
      </div>
    </Dialog>
  );
}
